/**
 * Sidebar — navegação lateral principal (visual Sphere).
 *
 * Larguras (sphere.css): 184 px textual · 76 px compacta (961–1180 px, nomes
 * acessíveis + tooltip) · ≤640 px vira gaveta aberta pelo botão de menu do topo
 * (`open`/`onClose`), com Escape e retorno de foco tratados no AppLayout.
 *
 * Ícones Lucide (licença ISC em apps/web/licenses/Lucide-ISC.txt), traço 1.75.
 * Tenant: a API não expõe nome de tenant (GET /settings só traz configuração
 * técnica); o cartão mostra a identificação da INSTÂNCIA (single-tenant), que é
 * configuração de implantação e fica em `theme/branding.js` (INSTANCE_TENANT).
 *
 * Recolher/expandir (2026-10-03): botão no rodapé da sidebar deixa o menu só com ícones
 * em qualquer largura acima de 1180 px (a preferência fica no navegador — AppLayout).
 * Entre 961 e 1180 px o menu já é compacto; no celular é gaveta — o botão não aparece.
 *
 * Acessibilidade: <nav> + aria-current="page" no item ativo.
 */
import { forwardRef } from 'react';
import {
  House, Gauge, BookOpen, History, Settings, GitFork, Radio, Users, Wallet, Activity,
  PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { SphereBrand } from '@/ui/sphere/SphereBrand';
import { NavaLogo } from '@/ui/sphere/NavaLogo';
import { INSTANCE_TENANT } from '@/theme/branding';

export const NAV_ITEMS = [
  { id: 'dashboard',      label: 'Dashboard',           icon: House },
  { id: 'project',        label: 'Gestão de Projetos',  icon: Gauge },
  { id: 'missionControl', label: 'Mission Control',      icon: Radio },
  { id: 'agentRoom',      label: 'Sala dos Agentes',    icon: Users },
  // Nome longo: o rótulo quebra em duas linhas em vez de cortar com reticências.
  { id: 'agentObservability', label: 'Observabilidade de Agentes', icon: Activity, quebra: true },
  // 'agentSuggest' (Sugestão de Agentes) desativado no menu por decisão do dono.
  { id: 'catalog',        label: 'Catálogo de Agents',  icon: BookOpen },
  { id: 'grafo',          label: 'Grafo do Repo',        icon: GitFork },
  { id: 'history',        label: 'Histórico',            icon: History },
  // 'contextAudit' (Auditoria de Contexto) e 'assistant' (Assistente) desativados no
  // menu por decisão do dono (2026-09-22). As telas e as rotas continuam de pé — só a
  // entrada no menu saiu, como já se fez com 'agentSuggest'.
  { id: 'usage',          label: 'Custos',               icon: Wallet },
  { id: 'settings',       label: 'Configurações',        icon: Settings },
];

function TenantCard() {
  const { name, tier } = INSTANCE_TENANT;
  return (
    <div className="sp-tenant">
      <p className="sp-tenant-eyebrow">Tenant</p>
      <div className="sp-tenant-name">
        <span title={name}>{name}</span>
        <span className="sp-dot sp-dot--success" aria-hidden="true" />
      </div>
      <div className="sp-tenant-sub">{tier}</div>
    </div>
  );
}

export const Sidebar = forwardRef(function Sidebar({
  currentScreen, navigate, onNavigate, compacta = false, onAlternar,
}, ref) {
  return (
    <aside className="sp-sidebar" id="sp-navigation" aria-label="Navegação principal" ref={ref}>
      <div className="sp-sidebar-brand">
        <span className="sp-brand-full"><SphereBrand size={92} /></span>
        <span className="sp-brand-compact"><SphereBrand size={54} compact /></span>
        {/* Logo institucional, somado à marca Sphere; some na barra compacta (76 px). */}
        <NavaLogo largura={92} className="sp-brand-nava" />
      </div>

      <nav aria-label="Menu principal">
        <ul className="sp-nav" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {NAV_ITEMS.map((item) => {
            const active = item.id === currentScreen;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className="sp-nav-item"
                  onClick={() => { navigate(item.id); onNavigate?.(); }}
                  aria-current={active ? 'page' : undefined}
                  aria-label={item.label}
                  data-tooltip={item.label}
                >
                  <item.icon size={20} strokeWidth={1.75} aria-hidden="true" />
                  <span className={item.quebra ? 'sp-nav-label sp-nav-label--quebra' : 'sp-nav-label'}>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <TenantCard />

      {onAlternar && (
        <button
          type="button"
          className="sp-nav-toggle"
          onClick={onAlternar}
          aria-controls="sp-navigation"
          aria-expanded={!compacta}
          aria-label={compacta ? 'Expandir menu' : 'Recolher menu'}
          data-tooltip={compacta ? 'Expandir menu' : 'Recolher menu'}
        >
          {compacta
            ? <PanelLeftOpen size={18} strokeWidth={1.75} aria-hidden="true" />
            : <PanelLeftClose size={18} strokeWidth={1.75} aria-hidden="true" />}
          <span className="sp-nav-label">{compacta ? 'Expandir menu' : 'Recolher menu'}</span>
        </button>
      )}
    </aside>
  );
});
